import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
    Bot, 
    Settings2, 
    Cpu, 
    Zap, 
    FlaskConical, 
    Factory,
    Check,
    AlertTriangle,
    RefreshCw,
    Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AISettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modelsData, setModelsData] = useState(null);
    const [activeModels, setActiveModels] = useState(null);
    
    // Selection state
    const [productionModel, setProductionModel] = useState('');
    const [testingModel, setTestingModel] = useState('');
    const [productionTemp, setProductionTemp] = useState(0.3);
    const [testingTemp, setTestingTemp] = useState(0.5);
    const [productionTokens, setProductionTokens] = useState(4096);
    const [testingTokens, setTestingTokens] = useState(8192);

    useEffect(() => {
        fetchModels();
        fetchActiveModels();
    }, []);

    const fetchModels = async () => {
        try {
            const response = await fetch(`${API_URL}/api/ai-models`, {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                setModelsData(data);
                
                // Set current selections
                if (data.active_production) {
                    setProductionModel(data.active_production.model_id);
                    setProductionTemp(data.active_production.temperature || 0.3);
                    setProductionTokens(data.active_production.max_tokens || 4096);
                } else if (data.defaults?.production) {
                    setProductionModel(data.defaults.production.model_id);
                    setProductionTemp(data.defaults.production.temperature);
                    setProductionTokens(data.defaults.production.max_tokens);
                }
                
                if (data.active_testing) {
                    setTestingModel(data.active_testing.model_id);
                    setTestingTemp(data.active_testing.temperature || 0.5);
                    setTestingTokens(data.active_testing.max_tokens || 8192);
                } else if (data.defaults?.testing) {
                    setTestingModel(data.defaults.testing.model_id);
                    setTestingTemp(data.defaults.testing.temperature);
                    setTestingTokens(data.defaults.testing.max_tokens);
                }
            }
        } catch (error) {
            console.error('Error fetching models:', error);
            toast.error('Failed to load AI models');
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveModels = async () => {
        try {
            const response = await fetch(`${API_URL}/api/ai-models/active`, {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                setActiveModels(data);
            }
        } catch (error) {
            console.error('Error fetching active models:', error);
        }
    };

    const saveModelSelection = async (mode, modelId, temperature, maxTokens) => {
        setSaving(true);
        try {
            const response = await fetch(`${API_URL}/api/ai-models/select`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    mode,
                    model_id: modelId,
                    temperature,
                    max_tokens: maxTokens
                })
            });
            
            const data = await response.json();
            if (data.success) {
                toast.success(`${mode.charAt(0).toUpperCase() + mode.slice(1)} model updated to ${data.display_name}`);
                fetchModels();
                fetchActiveModels();
            } else {
                toast.error(data.detail || 'Failed to update model');
            }
        } catch (error) {
            console.error('Error saving model:', error);
            toast.error('Failed to save model selection');
        } finally {
            setSaving(false);
        }
    };

    const getModelById = (modelId) => {
        return modelsData?.available_models?.find(m => m.model_id === modelId);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-zinc-950">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-950 text-white p-6">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-600/20 rounded-xl">
                            <Bot className="w-8 h-8 text-blue-400" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">AI Model Settings</h1>
                            <p className="text-zinc-400">Configure AI models for production and testing modes</p>
                        </div>
                    </div>
                    <Button 
                        variant="outline" 
                        onClick={() => { fetchModels(); fetchActiveModels(); }}
                        className="border-zinc-700"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                </div>

                {/* Current Status */}
                {activeModels && (
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Cpu className="w-5 h-5 text-green-400" />
                                Current Active Models
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Factory className="w-4 h-4 text-emerald-400" />
                                        <span className="text-sm text-zinc-400">Production Mode</span>
                                    </div>
                                    <p className="font-medium">{activeModels.production?.display_name || 'Not configured'}</p>
                                    <p className="text-xs text-zinc-500 mt-1">{activeModels.production?.model_id}</p>
                                    {activeModels.production?.source === 'default' && (
                                        <Badge variant="outline" className="mt-2 text-xs border-yellow-600 text-yellow-400">
                                            Using Default
                                        </Badge>
                                    )}
                                </div>
                                <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FlaskConical className="w-4 h-4 text-purple-400" />
                                        <span className="text-sm text-zinc-400">Testing Mode</span>
                                    </div>
                                    <p className="font-medium">{activeModels.testing?.display_name || 'Not configured'}</p>
                                    <p className="text-xs text-zinc-500 mt-1">{activeModels.testing?.model_id}</p>
                                    {activeModels.testing?.source === 'default' && (
                                        <Badge variant="outline" className="mt-2 text-xs border-yellow-600 text-yellow-400">
                                            Using Default
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Model Selection Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Production Model */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Factory className="w-5 h-5 text-emerald-400" />
                                Production Model
                            </CardTitle>
                            <CardDescription>
                                Used for all production AI operations. Recommend stable models only.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Model</Label>
                                <Select value={productionModel} onValueChange={setProductionModel}>
                                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                        <SelectValue placeholder="Select model" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                        {modelsData?.available_models?.map(model => (
                                            <SelectItem 
                                                key={model.model_id} 
                                                value={model.model_id}
                                                className="hover:bg-zinc-700"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span>{model.display_name}</span>
                                                    {model.is_preview && (
                                                        <Badge variant="outline" className="text-xs border-yellow-600 text-yellow-400">
                                                            Preview
                                                        </Badge>
                                                    )}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {getModelById(productionModel)?.is_preview && (
                                    <div className="flex items-center gap-2 text-yellow-400 text-sm">
                                        <AlertTriangle className="w-4 h-4" />
                                        Warning: Preview models not recommended for production
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label>Temperature: {productionTemp}</Label>
                                <Slider
                                    value={[productionTemp]}
                                    onValueChange={([val]) => setProductionTemp(val)}
                                    min={0}
                                    max={1}
                                    step={0.1}
                                    className="w-full"
                                />
                                <p className="text-xs text-zinc-500">Lower = more deterministic, Higher = more creative</p>
                            </div>

                            <div className="space-y-2">
                                <Label>Max Tokens</Label>
                                <Input
                                    type="number"
                                    value={productionTokens}
                                    onChange={(e) => setProductionTokens(parseInt(e.target.value) || 4096)}
                                    className="bg-zinc-800 border-zinc-700"
                                />
                            </div>

                            <Button 
                                onClick={() => saveModelSelection('production', productionModel, productionTemp, productionTokens)}
                                disabled={saving || !productionModel}
                                className="w-full bg-emerald-600 hover:bg-emerald-700"
                            >
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                                Save Production Model
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Testing Model */}
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <FlaskConical className="w-5 h-5 text-purple-400" />
                                Testing Model
                            </CardTitle>
                            <CardDescription>
                                Used for simulations and AI diagnostics. Preview models allowed.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Model</Label>
                                <Select value={testingModel} onValueChange={setTestingModel}>
                                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                        <SelectValue placeholder="Select model" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                        {modelsData?.available_models?.map(model => (
                                            <SelectItem 
                                                key={model.model_id} 
                                                value={model.model_id}
                                                className="hover:bg-zinc-700"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span>{model.display_name}</span>
                                                    {model.is_preview && (
                                                        <Badge variant="outline" className="text-xs border-purple-600 text-purple-400">
                                                            Preview
                                                        </Badge>
                                                    )}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Temperature: {testingTemp}</Label>
                                <Slider
                                    value={[testingTemp]}
                                    onValueChange={([val]) => setTestingTemp(val)}
                                    min={0}
                                    max={1}
                                    step={0.1}
                                    className="w-full"
                                />
                                <p className="text-xs text-zinc-500">Lower = more deterministic, Higher = more creative</p>
                            </div>

                            <div className="space-y-2">
                                <Label>Max Tokens</Label>
                                <Input
                                    type="number"
                                    value={testingTokens}
                                    onChange={(e) => setTestingTokens(parseInt(e.target.value) || 8192)}
                                    className="bg-zinc-800 border-zinc-700"
                                />
                            </div>

                            <Button 
                                onClick={() => saveModelSelection('testing', testingModel, testingTemp, testingTokens)}
                                disabled={saving || !testingModel}
                                className="w-full bg-purple-600 hover:bg-purple-700"
                            >
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                                Save Testing Model
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Available Models Reference */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Info className="w-5 h-5 text-blue-400" />
                            Available Models Reference
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-zinc-700">
                                        <th className="text-left py-2 px-3">Display Name</th>
                                        <th className="text-left py-2 px-3">Model ID</th>
                                        <th className="text-left py-2 px-3">Type</th>
                                        <th className="text-left py-2 px-3">Recommended For</th>
                                        <th className="text-left py-2 px-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modelsData?.available_models?.map(model => (
                                        <tr key={model.model_id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                                            <td className="py-2 px-3 font-medium">{model.display_name}</td>
                                            <td className="py-2 px-3 text-zinc-400 font-mono text-xs">{model.model_id}</td>
                                            <td className="py-2 px-3">
                                                {model.is_preview ? (
                                                    <Badge variant="outline" className="border-yellow-600 text-yellow-400">Preview</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="border-green-600 text-green-400">Stable</Badge>
                                                )}
                                            </td>
                                            <td className="py-2 px-3">
                                                <div className="flex gap-1">
                                                    {model.recommended_for?.map(mode => (
                                                        <Badge 
                                                            key={mode} 
                                                            variant="outline" 
                                                            className={mode === 'production' ? 'border-emerald-600 text-emerald-400' : 'border-purple-600 text-purple-400'}
                                                        >
                                                            {mode}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="py-2 px-3">
                                                {model.is_active_production && (
                                                    <Badge className="bg-emerald-600 mr-1">Production</Badge>
                                                )}
                                                {model.is_active_testing && (
                                                    <Badge className="bg-purple-600">Testing</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>

                {/* Info Box */}
                <Card className="bg-blue-900/20 border-blue-800">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                            <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                            <div className="text-sm text-blue-200">
                                <p className="font-medium mb-1">How Model Selection Works</p>
                                <ul className="list-disc list-inside space-y-1 text-blue-300">
                                    <li>Production mode uses stable models for reliable operations</li>
                                    <li>Testing mode allows preview models for experimentation</li>
                                    <li>Model changes take effect immediately (no redeploy required)</li>
                                    <li>All AI service calls dynamically read the selected model</li>
                                    <li>Temperature controls randomness (0.3 for production, 0.5 for testing recommended)</li>
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
